/**
 * Vanilla JS Collapse Manager
 * Replacement for Bootstrap collapse/accordion functionality
 * 
 * Usage:
 *   <button data-collapse-toggle="myCollapse">Toggle</button>
 *   <div id="myCollapse" class="hidden">...</div>
 *
 * Dynamic label on trigger based on state:
 *   Simple text (no icon):
 *   <button data-collapse-toggle="myCollapse"
 *           data-label-expanded="Hide Details"
 *           data-label-collapsed="Show Details">
 *     Show Details
 *   </button>
 *
 *   With icon (wrap text in .collapse-label):
 *   <button data-collapse-toggle="myCollapse"
 *           data-label-expanded="Hide Details"
 *           data-label-collapsed="Show Details">
 *     <i class="fa fa-fw fa-arrow-down"></i>
 *     <span class="collapse-label">Show Details</span>
 *   </button>
 *
 *   If data-label-expanded is omitted, the current trigger text is retained when expanding.
 *   If data-label-collapsed is omitted, the current trigger text is retained when collapsing.
 */

var CollapseManager = (function() {
  'use strict';

  var activeCollapses = {};
  var listenerAttached = false;

  /**
   * Toggle collapse state
   */
  function toggle(collapseId, triggerElement) {
    var collapse = document.getElementById(collapseId);
    if (!collapse) {
      console.error('Collapse element not found:', collapseId);
      return;
    }

    var isHidden = collapse.classList.contains('hidden') || 
                   !collapse.classList.contains('block');
    
    if (isHidden) {
      expand(collapseId, triggerElement);
    } else {
      collapse_element(collapseId, triggerElement);
    }
  }

  /**
   * Update trigger label based on collapse state
   */
  function updateTriggerLabel(triggerElement, state) {
    var attr = state === 'expanded' ? 'data-label-expanded' : 'data-label-collapsed';
    var label = triggerElement.getAttribute(attr);

    if (label === null || label.trim() === '') return;

    var labelSpan = triggerElement.querySelector('.collapse-label');
    if (labelSpan) {
      labelSpan.textContent = label;
    } else {
      triggerElement.textContent = label;
    }
  }

  /**
   * Update all trigger elements for a collapse target
   */
  function updateAllTriggers(collapseId, state) {
    var isExpanded = state === 'expanded';
    var triggers = document.querySelectorAll('[data-collapse-toggle="' + collapseId + '"]');
    triggers.forEach(function(trigger) {
      trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      if (isExpanded) {
        trigger.classList.remove('collapsed');
      } else {
        trigger.classList.add('collapsed');
      }
      updateTriggerLabel(trigger, state);
    });
  }

  /**
   * Expand a collapsed element
   */
  function expand(collapseId, triggerElement) {
    var collapse = document.getElementById(collapseId);
    if (!collapse) return;

    // Handle accordion behavior (data-parent attribute)
    var parent = triggerElement ? triggerElement.getAttribute('data-parent') : null;
    if (parent) {
      // Close other collapses in the same parent
      var parentElement = document.querySelector(parent);
      if (parentElement) {
        var siblings = parentElement.querySelectorAll('[data-collapse-toggle]');
        siblings.forEach(function(sibling) {
          if (sibling !== triggerElement) {
            var siblingTarget = sibling.getAttribute('data-collapse-toggle');
            collapse_element(siblingTarget, sibling);
          }
        });
      }
    }

    // Get the natural height
    collapse.classList.remove('hidden');
    collapse.style.height = '0';
    collapse.style.overflow = 'hidden';
    
    var height = collapse.scrollHeight;
    
    // Animate to full height
    collapse.style.transition = 'height 300ms ease-in-out';
    collapse.style.height = height + 'px';
    
    setTimeout(function() {
      collapse.style.height = '';
      collapse.style.overflow = '';
      collapse.classList.add('block');
      activeCollapses[collapseId] = true;
    }, 300);

    // Update all triggers for this collapse
    updateAllTriggers(collapseId, 'expanded');

    // Emit custom event
    var event = new CustomEvent('collapse:expanded', { detail: { collapseId: collapseId } });
    document.dispatchEvent(event);
  }

  /**
   * Collapse an expanded element
   */
  function collapse_element(collapseId, triggerElement) {
    var collapse = document.getElementById(collapseId);
    if (!collapse) return;

    var height = collapse.scrollHeight;
    
    // Set explicit height first
    collapse.style.height = height + 'px';
    collapse.style.overflow = 'hidden';
    
    // Force reflow
    collapse.offsetHeight;
    
    // Animate to 0 height
    collapse.style.transition = 'height 300ms ease-in-out';
    collapse.style.height = '0';
    
    setTimeout(function() {
      collapse.classList.remove('block');
      collapse.classList.add('hidden');
      collapse.style.height = '';
      collapse.style.overflow = '';
      delete activeCollapses[collapseId];
    }, 300);

    // Update all triggers for this collapse
    updateAllTriggers(collapseId, 'collapsed');

    // Emit custom event
    var event = new CustomEvent('collapse:collapsed', { detail: { collapseId: collapseId } });
    document.dispatchEvent(event);
  }

  /**
   * Initialize all collapse triggers
   */
  function init() {
    if (!listenerAttached) {
      document.addEventListener('click', function(e) {
        var toggleElement = e.target.closest('[data-collapse-toggle]');
        if (!toggleElement) return;
        e.preventDefault();
        toggle(toggleElement.getAttribute('data-collapse-toggle'), toggleElement);
      });
      listenerAttached = true;
    }

    // Initialize collapsed state
    var collapses = document.querySelectorAll('[data-collapse]');
    collapses.forEach(function(collapse) {
      if (!collapse.classList.contains('show')) {
        collapse.classList.add('hidden');
      }
    });
  }

  // Public API
  return {
    init: init,
    toggle: toggle,
    expand: expand,
    collapse: collapse_element
  };
})();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', CollapseManager.init);
} else {
  CollapseManager.init();
}
